import { useCallback, type CSSProperties, type FC } from "react";
import React, { memo, useEffect, useMemo } from "react";
import Icon from "../../../../../../shared-components/css-modules/toyota/atoms/Icon";
import styles from "./Exterior360.module.scss";
import spinContainerStyles from "../SpinContainer.module.scss";
import { useSpin } from "../../../../../hooks/useAphVisual";
import type { ImageOptionsType } from "../../../../../../../../common-deprecated/settings/utils/ccisSettingUtils";
import { trackActivateExterior360Spin } from "../../../../../utils/tracking";
import { Loader } from "../../../../../../shared-components/css-modules/toyota/atoms/Loader";
import { useCarConfigSelector } from "../../../../../redux/store";
import Button from "../../../../../../shared-components/css-modules/toyota/atoms/Button";
import a11y from "../../../../../../shared-components/css-modules/toyota/utils/Accessibility.module.scss";
import { CarConfigLabel } from "../../../../../utils/labelUtils";

const imgCount = 36;

const Exterior360: FC<{
    index?: number;
    imageOptions?: ImageOptionsType;
}> = ({ index = 0, imageOptions }) => {
    const section = useCarConfigSelector((state) => state.carConfigNavigation.activeSection);
    const { frameProps, cacheKey, getImageSrc } = useSpin(index, imageOptions);
    const {
        isActive,
        containerProps,
        currentFrame,
        showPrevImg,
        imageCache,
        previousImg,
        isLoading,
        cacheImageRef,
        setFrame,
    } = frameProps;

    useEffect(() => {
        if (isActive) {
            trackActivateExterior360Spin(section);
        }
    }, [isActive]);

    const rotateLeft = useCallback(() => {
        // This calculation is to ensure that the frame is always between the limits of the 360 image frames
        setFrame((prevFrame) => (prevFrame - 1 + imgCount) % imgCount);
    }, [setFrame]);

    const rotateRight = useCallback(() => {
        setFrame((prevFrame) => (prevFrame + 1) % imgCount);
    }, [setFrame]);

    const style = useMemo<CSSProperties>(() => ({ position: "relative" }), []);
    const styleInner = useMemo<CSSProperties>(
        () => ({ position: "relative", touchAction: "none", userSelect: "none" }),
        [],
    );

    return useMemo(
        () => (
            <div style={style} key="exterior-360">
                <div {...containerProps} style={styleInner}>
                    {!isActive && !isLoading && (
                        <div className={styles.icon}>
                            <Icon variant="360-spin" aria-hidden="true" />
                        </div>
                    )}

                    {isLoading && (
                        <div className={styles.icon}>
                            <Loader />
                        </div>
                    )}

                    <img
                        ref={cacheImageRef}
                        style={{
                            position: "relative",
                            zIndex: 5,
                            width: "100%",
                        }}
                        // set the index src manually to preload serverside
                        src={(index && getImageSrc(index).src) || ""}
                        alt=""
                    />

                    {previousImg.src && (
                        <img
                            data-image="previousimg"
                            key={`${previousImg.cacheKey}-${previousImg.frame}`}
                            alt=""
                            {...getImageSrc(previousImg.frame)}
                            style={{
                                opacity: showPrevImg ? 1 : 0,
                                transition: showPrevImg ? "opacity 0.3s" : "none",
                                position: "absolute",
                                left: 0,
                                top: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: 5,
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                            }}
                        />
                    )}

                    {imageCache.current
                        .filter((image) => image.loaded || image.loading)
                        .map((image) => (
                            <img
                                data-image="current"
                                key={`${cacheKey}-${image.frame}`}
                                alt=""
                                {...getImageSrc(image.frame)}
                                style={{
                                    position: "absolute",
                                    opacity: image.frame === currentFrame && !showPrevImg ? 1 : 0,
                                    left: 0,
                                    top: 0,
                                    right: 0,
                                    bottom: 0,
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                }}
                            />
                        ))}
                </div>
                <div className={styles["spinner-buttons"]}>
                    {/* Taking from container styles for the focus state */}
                    <Button variant="clean" className={spinContainerStyles["rotate-button"]} onClick={rotateLeft}>
                        <Icon variant="360-spin-left" sizing="md" aria-hidden="true" />

                        <span className={a11y["sr-only"]}>
                            <CarConfigLabel label="ariaRotateSpinnerLeft" />
                        </span>
                    </Button>

                    <Button variant="clean" className={spinContainerStyles["rotate-button"]} onClick={rotateRight}>
                        <Icon variant="360-spin-right" sizing="md" aria-hidden="true" />

                        <span className={a11y["sr-only"]}>
                            <CarConfigLabel label="ariaRotateSpinnerRight" />
                        </span>
                    </Button>
                </div>
            </div>
        ),
        [showPrevImg, currentFrame, isLoading, isActive],
    );
};

export default memo(Exterior360);
